// src/components/PayrollGenerator.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  AlertTitle,
  Tooltip,
  Avatar,
  alpha,
  useMediaQuery,
  useTheme,
  Stack,
  Alert,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormHelperText,
  Backdrop,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  RemoveCircle as DeductIcon,
  CheckCircle as CheckIcon,
  History as HistoryIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  FileDownload as FileDownloadIcon,
  CardGiftcard as BonusIcon,
  Paid as PaidIcon,
  Close as CloseIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';


import Sidebar from '../../../../components/sidebars/Sidebar_Gm';
import TopNav from '../../../../components/TopNav_hotelStaff';

// ==================== CONSTANTS & CONFIGURATION ====================
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const YEARS = [2024, 2025, 2026, 2027, 2028];

// Nigerian Bank Sort Codes Mapping (CBN Standard)
const BANK_SORT_CODES = {
  'Access Bank': '044150000',
  'Access': '044150000',
  'Access Bank Plc': '044150000',
  'Access Bank Nigeria': '044150000',
  'Citibank': '023150000',
  'Citibank Nigeria': '023150000',
  'Ecobank': '050150000',
  'Eco Bank': '050150000',
  'Ecobank Nigeria': '050150000',
  'Fidelity Bank': '070150000',
  'Fidelity': '070150000',
  'Fidelity Bank Plc': '070150000',
  'First Bank': '011150000',
  'First Bank of Nigeria': '011150000',
  'First Bank Plc': '011150000',
  'FCMB': '033150000',
  'First City Monument Bank': '033150000',
  'Globus Bank': '103150000',
  'GTB': '058150000',
  'GTBank': '058150000',
  'GTCO': '058150000',
  'Guaranty Trust Bank': '058150000',
  'Heritage Bank': '030150000',
  'Jaiz Bank': '301150000',
  'Jaiz': '301150000',
  'Keystone Bank': '082150000',
  'Keystone': '082150000',
  'Polaris Bank': '076150000',
  'Polaris': '076150000',
  'Providus Bank': '101150000',
  'Stanbic IBTC': '221150000',
  'Stanbic': '221150000',
  'Standard Chartered': '068150000',
  'Sterling Bank': '232150000',
  'Sterling': '232150000',
  'SunTrust Bank': '100150000',
  'Titan Trust Bank': '090150000',
  'Union Bank': '032150000',
  'Union': '032150000',
  'United Bank for Africa': '033150000',
  'UBA': '033150000',
  'Unity Bank': '215150000',
  'Wema Bank': '035150000',
  'WEMA': '035150000',
  'Zenith Bank': '057150000',
  'Zenith': '057150000',
  // Digital banks (no sort code, leave empty)
  'OPay': '',
  'Moniepoint': '',
  'Monie Point': '',
  'Paga': '',
  'Palmpay': '',
  'PalmPay': '',
};

// Allowed user roles for manager operations
// Allowed user roles for manager operations
const MANAGER_ROLES = [
  'General_Manager',   // From staff table - Ahmed Musa
  'gm',                // Alternative
  'hr',                // From staff table - Adole Moses  
  'Account',           // From staff table - Jubril Ado
  'admin',             
  'manager',
  'cashier',
  'Requisition'
];
// Input validation patterns
const VALIDATION_PATTERNS = {
  phone: /^[0-9]{10,15}$/,
  amount: /^\d+(\.\d{0,2})?$/,
  text: /^[a-zA-Z0-9\s\-_,.!?'"()]{2,500}$/,
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Sanitize string input to prevent XSS attacks
 */
const sanitizeString = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str
    .trim()
    .replace(/[<>]/g, '') // Remove < and > to prevent HTML injection
    .replace(/[&]/g, '&amp;')
    .replace(/["]/g, '&quot;')
    .replace(/[']/g, '&#x27;')
    .replace(/[/]/g, '&#x2F;');
};

/**
 * Get bank sort code from bank name with fuzzy matching
 */
const getBankSortCode = (bankName) => {
  if (!bankName || typeof bankName !== 'string') {
    console.debug('[Payroll] getBankSortCode: Empty bank name');
    return '';
  }
  
  const normalizedBankName = bankName.trim();
  console.debug(`[Payroll] Looking up sort code for bank: "${normalizedBankName}"`);
  
  // Exact match first
  if (BANK_SORT_CODES[normalizedBankName]) {
    console.debug(`[Payroll] Exact match found: ${BANK_SORT_CODES[normalizedBankName]}`);
    return BANK_SORT_CODES[normalizedBankName];
  }
  
  // Case-insensitive partial match
  for (const [key, code] of Object.entries(BANK_SORT_CODES)) {
    if (normalizedBankName.toLowerCase().includes(key.toLowerCase()) ||
        key.toLowerCase().includes(normalizedBankName.toLowerCase())) {
      console.debug(`[Payroll] Partial match: "${key}" -> "${code}"`);
      return code;
    }
  }
  
  console.debug(`[Payroll] No sort code found for bank: "${normalizedBankName}"`);
  return '';
};

/**
 * Format currency for Nigerian Naira
 */
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '₦0.00';
  const num = parseFloat(amount);
  if (isNaN(num)) return '₦0.00';
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

/**
 * Format date safely
 */
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-NG');
  } catch (error) {
    console.error('[Payroll] Date formatting error:', error);
    return 'N/A';
  }
};

/**
 * Validate deduction amount against salary
 */
const validateDeductionAmount = (amount, salary) => {
  const numAmount = parseFloat(amount);
  const numSalary = parseFloat(salary);
  
  if (isNaN(numAmount)) {
    return { valid: false, message: 'Please enter a valid number' };
  }
  if (numAmount <= 0) {
    return { valid: false, message: 'Amount must be greater than 0' };
  }
  if (numSalary > 0 && numAmount > numSalary) {
    return { valid: false, message: `Amount cannot exceed salary (${formatCurrency(numSalary)})` };
  }
  if (numSalary > 0 && numAmount > numSalary * 0.5) {
    return { valid: true, warning: `This deduction exceeds 50% of the employee's salary. Please review.` };
  }
  
  return { valid: true, message: 'Valid amount' };
};

/**
 * Validate bonus amount
 */
const validateBonusAmount = (amount) => {
  const numAmount = parseFloat(amount);
  
  if (isNaN(numAmount)) {
    return { valid: false, message: 'Please enter a valid number' };
  }
  if (numAmount <= 0) {
    return { valid: false, message: 'Amount must be greater than 0' };
  }
  if (numAmount > 1000000) {
    return { valid: true, warning: 'Amount exceeds ₦1,000,000. Please ensure this is approved by management.' };
  }
  
  return { valid: true, message: 'Valid amount' };
};

/**
 * Validate reason text
 */
const validateReason = (reason, type = 'deduction') => {
  if (!reason || typeof reason !== 'string') {
    return { valid: false, message: `${type === 'deduction' ? 'Deduction' : 'Bonus'} reason is required` };
  }
  
  const trimmed = reason.trim();
  if (trimmed.length < 3) {
    return { valid: false, message: `Please provide a detailed reason (minimum 3 characters)` };
  }
  if (trimmed.length > 500) {
    return { valid: false, message: `Reason cannot exceed 500 characters` };
  }
  
  return { valid: true, message: 'Valid reason' };
};

/**
 * Log security events to console (for debugging)
 */
const logSecurityEvent = (eventType, details) => {
  const logEntry = {
    timestamp: new Date().toISOString(),
    eventType,
    details,
    userAgent: navigator.userAgent,
    url: window.location.href,
  };
  console.debug(`[SECURITY] ${eventType}:`, logEntry);
};

/**
 * Check if user has manager permissions
 */
/**
 * Check if user has manager permissions
 */
const isManager = (userRole) => {
  // Convert to lowercase for case-insensitive comparison
  const roleLower = (userRole || '').toLowerCase();
  
  const hasAccess = MANAGER_ROLES.some(role => 
    role.toLowerCase() === roleLower
  );
  
  console.debug(`[Payroll] Role check: "${userRole}" -> Has access: ${hasAccess}`);
  return hasAccess;
};
// ==================== MAIN COMPONENT ====================
const PayrollGenerator = () => {
  // ==================== STATE DECLARATIONS ====================
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Data states
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [payrollData, setPayrollData] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [activeTab, setActiveTab] = useState(0);
  
  // Related data states
  const [payrollHistory, setPayrollHistory] = useState([]);
  const [deductionsList, setDeductionsList] = useState([]);
  const [bonusesList, setBonusesList] = useState([]);
  const [missingAccounts, setMissingAccounts] = useState([]);
  const [zeroSalaryStaff, setZeroSalaryStaff] = useState([]);
  
  // UI states
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showMissingAlert, setShowMissingAlert] = useState(false);
  const [showZeroAlert, setShowZeroAlert] = useState(false);
  const [userRole, setUserRole] = useState('gm');
  
  // Dialog states
  const [deductionDialogOpen, setDeductionDialogOpen] = useState(false);
  const [bonusDialogOpen, setBonusDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  
  // Form states with validation
  const [deductionAmount, setDeductionAmount] = useState('');
  const [deductionReason, setDeductionReason] = useState('');
  const [deductionValidation, setDeductionValidation] = useState({ valid: false, message: '', warning: null });
  
  const [bonusAmount, setBonusAmount] = useState('');
  const [bonusReason, setBonusReason] = useState('');
  const [bonusValidation, setBonusValidation] = useState({ valid: false, message: '', warning: null });
  
  // Loading states
  const [markingPaid, setMarkingPaid] = useState({});
  
  // ==================== DERIVED VALUES ====================
  const isManagerOrAdmin = useMemo(() => isManager(userRole), [userRole]);
  
  const uniqueDepartments = useMemo(() => {
    const depts = ['all', ...new Set(payrollData.map(emp => emp.department).filter(Boolean))];
    console.debug(`[Payroll] Unique departments: ${depts.length - 1} found`);
    return depts;
  }, [payrollData]);
  
  const filteredData = useMemo(() => {
    const filtered = payrollData.filter(emp => {
      const matchesSearch = searchTerm === '' || 
        emp.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.phone?.includes(searchTerm);
      const matchesDept = departmentFilter === 'all' || emp.department === departmentFilter;
      return matchesSearch && matchesDept;
    });
    console.debug(`[Payroll] Filtered data: ${filtered.length} of ${payrollData.length} employees`);
    return filtered;
  }, [payrollData, searchTerm, departmentFilter]);
  
  const totalBonuses = useMemo(() => {
    return bonusesList.reduce((sum, b) => sum + (parseFloat(b.recent_bonus) || 0), 0);
  }, [bonusesList]);
  
  const unpaidBonuses = useMemo(() => {
    return bonusesList.filter(b => b.bonus_paid !== 1);
  }, [bonusesList]);
  
  const totalUnpaidBonuses = useMemo(() => {
    return unpaidBonuses.reduce((sum, b) => sum + (parseFloat(b.recent_bonus) || 0), 0);
  }, [unpaidBonuses]);
  
  // ==================== VALIDATION EFFECTS ====================
  useEffect(() => {
    if (deductionAmount) {
      const salary = selectedStaff?.base_salary || 0;
      const validation = validateDeductionAmount(deductionAmount, salary);
      setDeductionValidation(validation);
    } else {
      setDeductionValidation({ valid: false, message: '', warning: null });
    }
  }, [deductionAmount, selectedStaff]);
  
  useEffect(() => {
    if (bonusAmount) {
      const validation = validateBonusAmount(bonusAmount);
      setBonusValidation(validation);
    } else {
      setBonusValidation({ valid: false, message: '', warning: null });
    }
  }, [bonusAmount]);
  
  // ==================== API CALLS ====================
  const getApiBaseUrl = () => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
    console.debug(`[Payroll] API Base URL: ${baseUrl || 'default'}`);
    return baseUrl;
  };
  
  const fetchPayrollData = useCallback(async () => {
    setLoading(true);
    const startTime = performance.now();
    console.debug(`[Payroll] Fetching payroll data for ${MONTHS[selectedMonth - 1]} ${selectedYear}`);
    
    try {
      const baseUrl = getApiBaseUrl();
      const url = `${baseUrl}/hotel/HR/payroll_api.php?action=get_payroll_data&month=${selectedMonth}&year=${selectedYear}`;
      console.debug(`[Payroll] GET request: ${url}`);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);
      
      console.debug(`[Payroll] Response status: ${response.status}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      console.debug(`[Payroll] API Response received in ${(performance.now() - startTime).toFixed(2)}ms`);
      
      if (result.success) {
        const employees = result.data || [];
        setPayrollData(employees);
        setStats(result.stats);
        console.debug(`[Payroll] Loaded ${employees.length} employees`);
        
        if (result.warnings && result.warnings.length > 0) {
          console.debug(`[Payroll] Warnings: ${result.warnings.length}`, result.warnings);
          setSnackbar({ 
            open: true, 
            message: `${result.warnings.length} warning(s) found. Check payroll summary.`, 
            severity: 'warning' 
          });
        }
      } else {
        console.error(`[Payroll] API Error: ${result.message}`);
        setSnackbar({ open: true, message: result.message || 'Failed to load data', severity: 'error' });
      }
      
      // Fetch bonuses
      await fetchBonusesList();
      
      // Fetch deductions
      await fetchDeductionsList();
      
    } catch (err) {
      console.error(`[Payroll] Fetch error:`, err);
      if (err.name === 'AbortError') {
        setSnackbar({ open: true, message: 'Request timeout. Please try again.', severity: 'error' });
      } else {
        setSnackbar({ open: true, message: `Failed to load payroll data: ${err.message}`, severity: 'error' });
      }
    } finally {
      setLoading(false);
      console.debug(`[Payroll] Fetch completed in ${(performance.now() - startTime).toFixed(2)}ms`);
    }
  }, [selectedMonth, selectedYear]);
  
  const fetchBonusesList = useCallback(async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const url = `${baseUrl}/hotel/HR/payroll_api.php?action=get_bonuses&month=${selectedMonth}&year=${selectedYear}`;
      console.debug(`[Payroll] Fetching bonuses from: ${url}`);
      
      const response = await fetch(url);
      const result = await response.json();
      
      if (result.success) {
        setBonusesList(result.data);
        console.debug(`[Payroll] Loaded ${result.data.length} bonuses`);
      }
    } catch (err) {
      console.error('[Payroll] Error fetching bonuses:', err);
    }
  }, [selectedMonth, selectedYear]);
  
  const fetchDeductionsList = useCallback(async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const url = `${baseUrl}/hotel/HR/payroll_api.php?action=get_deductions_by_month&month=${selectedMonth}&year=${selectedYear}`;
      console.debug(`[Payroll] Fetching deductions from: ${url}`);
      
      const response = await fetch(url);
      const result = await response.json();
      
      if (result.success) {
        setDeductionsList(result.data);
        console.debug(`[Payroll] Loaded ${result.data.length} deductions`);
      }
    } catch (err) {
      console.error('[Payroll] Error fetching deductions:', err);
    }
  }, [selectedMonth, selectedYear]);
  
  const fetchPayrollHistory = useCallback(async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const url = `${baseUrl}/hotel/HR/payroll_api.php?action=get_payroll_history`;
      console.debug(`[Payroll] Fetching payroll history`);
      
      const response = await fetch(url);
      const result = await response.json();
      
      if (result.success) {
        setPayrollHistory(result.data);
        console.debug(`[Payroll] Loaded ${result.data.length} history records`);
      }
    } catch (err) {
      console.error('[Payroll] Error fetching history:', err);
    }
  }, []);
  
  const fetchMissingAccounts = useCallback(async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const url = `${baseUrl}/hotel/HR/payroll_api.php?action=get_missing_accounts`;
      console.debug(`[Payroll] Fetching missing accounts`);
      
      const response = await fetch(url);
      const result = await response.json();
      
      if (result.success && result.data && result.data.length > 0) {
        setMissingAccounts(result.data);
        setShowMissingAlert(true);
        console.debug(`[Payroll] ${result.data.length} staff missing bank details`);
      }
    } catch (err) {
      console.error('[Payroll] Error fetching missing accounts:', err);
    }
  }, []);
  
  const fetchZeroSalaryStaff = useCallback(async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const url = `${baseUrl}/hotel/HR/payroll_api.php?action=get_zero_salary_staff`;
      console.debug(`[Payroll] Fetching zero salary staff`);
      
      const response = await fetch(url);
      const result = await response.json();
      
      if (result.success && result.data && result.data.length > 0) {
        setZeroSalaryStaff(result.data);
        setShowZeroAlert(true);
        console.debug(`[Payroll] ${result.data.length} staff with zero salary`);
      }
    } catch (err) {
      console.error('[Payroll] Error fetching zero salary staff:', err);
    }
  }, []);
  
  // ==================== INITIALIZATION ====================
  useEffect(() => {
    // Get user role from localStorage
    try {
      const userProfile = localStorage.getItem('userProfile');
      if (userProfile) {
        const profile = JSON.parse(userProfile);
        setUserRole(profile.user_type || 'gm');
        console.debug(`[Payroll] User role loaded: ${profile.user_type}`);
        logSecurityEvent('USER_ROLE_LOADED', { role: profile.user_type });
      }
    } catch (e) {
      console.error('[Payroll] Error parsing user profile:', e);
    }
  }, []);
  
  useEffect(() => {
    fetchPayrollData();
    fetchPayrollHistory();
    fetchMissingAccounts();
    fetchZeroSalaryStaff();
  }, [fetchPayrollData, fetchPayrollHistory, fetchMissingAccounts, fetchZeroSalaryStaff]);
  
  // ==================== HANDLER FUNCTIONS ====================
  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
    console.debug(`[Payroll] Sidebar toggled: ${!sidebarOpen}`);
  };
  
  const handleAddDeduction = async () => {
    logSecurityEvent('DEDUCTION_ATTEMPT', { staff: selectedStaff?.phone, amount: deductionAmount });
    
    if (!isManagerOrAdmin) {
      console.warn('[Payroll] Access denied: Non-manager attempted to add deduction');
      setSnackbar({ open: true, message: 'Access denied. Only managers can add deductions.', severity: 'error' });
      return;
    }
    
    if (!selectedStaff) {
      console.warn('[Payroll] Deduction attempt with no staff selected');
      setSnackbar({ open: true, message: 'No staff selected', severity: 'error' });
      return;
    }
    
    const amountValidation = validateDeductionAmount(deductionAmount, selectedStaff.base_salary);
    if (!amountValidation.valid) {
      setSnackbar({ open: true, message: amountValidation.message, severity: 'error' });
      return;
    }
    
    const reasonValidation = validateReason(deductionReason, 'deduction');
    if (!reasonValidation.valid) {
      setSnackbar({ open: true, message: reasonValidation.message, severity: 'error' });
      return;
    }
    
    setLoading(true);
    const startTime = performance.now();
    
    try {
      const baseUrl = getApiBaseUrl();
      const payload = {
        phone: sanitizeString(selectedStaff.phone),
        amount: parseFloat(deductionAmount),
        remark: sanitizeString(deductionReason),
        payroll_month: selectedMonth,
        payroll_year: selectedYear,
        requested_by: localStorage.getItem('userName') || 'HR'
      };
      
      console.debug(`[Payroll] POST deduction request:`, { ...payload, amount: payload.amount });
      
      const response = await fetch(`${baseUrl}/hotel/HR/payroll_api.php?action=add_deduction`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      });
      
      const result = await response.json();
      console.debug(`[Payroll] Deduction response in ${(performance.now() - startTime).toFixed(2)}ms:`, result);
      
      if (result.success) {
        setSnackbar({ open: true, message: result.message, severity: 'success' });
        setDeductionDialogOpen(false);
        setDeductionAmount('');
        setDeductionReason('');
        setSelectedStaff(null);
        fetchPayrollData();
        logSecurityEvent('DEDUCTION_ADDED', { 
          staff: selectedStaff.phone, 
          amount: deductionAmount,
          deduction_id: result.deduction_id 
        });
      } else {
        setSnackbar({ open: true, message: result.message, severity: 'error' });
        logSecurityEvent('DEDUCTION_FAILED', { staff: selectedStaff.phone, error: result.message });
      }
    } catch (err) {
      console.error('[Payroll] Error adding deduction:', err);
      setSnackbar({ open: true, message: `Failed to add deduction: ${err.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddBonus = async () => {
    logSecurityEvent('BONUS_ATTEMPT', { staff: selectedStaff?.phone, amount: bonusAmount });
    
    if (!isManagerOrAdmin) {
      console.warn('[Payroll] Access denied: Non-manager attempted to add bonus');
      setSnackbar({ open: true, message: 'Access denied. Only managers can add bonuses.', severity: 'error' });
      return;
    }
    
    if (!selectedStaff) {
      setSnackbar({ open: true, message: 'No staff selected', severity: 'error' });
      return;
    }
    
    const amountValidation = validateBonusAmount(bonusAmount);
    if (!amountValidation.valid) {
      setSnackbar({ open: true, message: amountValidation.message, severity: 'error' });
      return;
    }
    
    if (amountValidation.warning) {
      const confirmWarning = window.confirm(amountValidation.warning + '\n\nDo you want to proceed?');
      if (!confirmWarning) return;
    }
    
    const reasonValidation = validateReason(bonusReason, 'bonus');
    if (!reasonValidation.valid) {
      setSnackbar({ open: true, message: reasonValidation.message, severity: 'error' });
      return;
    }
    
    setLoading(true);
    const startTime = performance.now();
    
    try {
      const baseUrl = getApiBaseUrl();
      
      // First, get employee_id from salary table
      console.debug(`[Payroll] Fetching employee ID for phone: ${selectedStaff.phone}`);
      const salaryResponse = await fetch(`${baseUrl}/hotel/HR/salary_api.php?action=get_employee_id&phone=${encodeURIComponent(selectedStaff.phone)}`);
      const salaryResult = await salaryResponse.json();
      
      let employeeId = null;
      if (salaryResult.success && salaryResult.employee_id) {
        employeeId = salaryResult.employee_id;
      } else {
        // Fallback: try alternative endpoint
        const altResponse = await fetch(`${baseUrl}/hotel/HR/common_api.php?action=get_salary_id&phone=${encodeURIComponent(selectedStaff.phone)}`);
        const altResult = await altResponse.json();
        employeeId = altResult.success ? altResult.id : null;
      }
      
      if (!employeeId) {
        throw new Error('Could not find employee record for this staff member');
      }
      
      console.debug(`[Payroll] Found employee_id: ${employeeId}`);
      
      const payload = {
        action: 'add_bonus',
        employee_id: parseInt(employeeId),
        amount: parseFloat(bonusAmount),
        reason: sanitizeString(bonusReason),
        month: selectedMonth,
        year: selectedYear,
        requested_by: localStorage.getItem('userName') || 'HR'
      };
      
      console.debug(`[Payroll] POST bonus request:`, { ...payload, amount: payload.amount });
      
      const response = await fetch(`${baseUrl}/hotel/HR/bonus_api.php`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      });
      
      const result = await response.json();
      console.debug(`[Payroll] Bonus response in ${(performance.now() - startTime).toFixed(2)}ms:`, result);
      
      if (result.success) {
        setSnackbar({ open: true, message: result.message, severity: 'success' });
        setBonusDialogOpen(false);
        setBonusAmount('');
        setBonusReason('');
        setSelectedStaff(null);
        fetchPayrollData();
        await fetchBonusesList();
        logSecurityEvent('BONUS_ADDED', { 
          staff: selectedStaff.phone, 
          amount: bonusAmount,
          bonus_id: result.bonus_id 
        });
      } else {
        setSnackbar({ open: true, message: result.message, severity: 'error' });
        logSecurityEvent('BONUS_FAILED', { staff: selectedStaff.phone, error: result.message });
      }
    } catch (err) {
      console.error('[Payroll] Error adding bonus:', err);
      setSnackbar({ open: true, message: `Failed to add bonus: ${err.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
// ==================== UPDATED handleMarkBonusPaid FUNCTION ====================
const handleMarkBonusPaid = async (bonusId, staffName, bonusAmountValue) => {
  logSecurityEvent('MARK_PAID_ATTEMPT', { bonus_id: bonusId, staff: staffName });
  
  if (!isManagerOrAdmin) {
    console.warn('[Payroll] Access denied: Non-manager attempted to mark bonus paid');
    setSnackbar({ open: true, message: 'Access denied. Only managers can mark bonuses as paid.', severity: 'error' });
    return;
  }
  
  // Confirm before marking as paid
  const confirmMark = window.confirm(
    `Are you sure you want to mark this bonus of ${formatCurrency(bonusAmountValue)} for ${staffName} as PAID?\n\n` +
    `This action cannot be undone.`
  );
  if (!confirmMark) return;
  
  setMarkingPaid(prev => ({ ...prev, [bonusId]: true }));
  const startTime = performance.now();
  
  try {
    const baseUrl = getApiBaseUrl();
    const payload = {
      id: parseInt(bonusId),
      paid_by: localStorage.getItem('userName') || 'Manager'
    };
    
    console.debug(`[Payroll] PATCH mark bonus paid:`, payload);
    
    const response = await fetch(`${baseUrl}/hotel/HR/payroll_api.php`, {
      method: 'PATCH',
      headers: { 
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload)
    });
    
    const result = await response.json();
    console.debug(`[Payroll] Mark paid response in ${(performance.now() - startTime).toFixed(2)}ms:`, result);
    
    if (result.success) {
      // Update local state IMMEDIATELY
      setBonusesList(prevList => 
        prevList.map(bonus => {
          if (bonus.id === bonusId) {
            console.debug(`[Payroll] Updating bonus ${bonusId} from paid=${bonus.bonus_paid} to paid=1`);
            return { 
              ...bonus, 
              bonus_paid: 1,
              is_paid: 1,
              paid_date: result.paid_date || new Date().toISOString().split('T')[0],
              paid_by: result.paid_by || localStorage.getItem('userName') || 'Manager'
            };
          }
          return bonus;
        })
      );
    
        setSnackbar({ open: true, message: result.message, severity: 'success' });
        fetchPayrollData();
        logSecurityEvent('BONUS_MARKED_PAID', { bonus_id: bonusId, staff: staffName });
      } else {
        setSnackbar({ open: true, message: result.message || 'Failed to mark bonus as paid', severity: 'error' });
      }
    } catch (err) {
      console.error('[Payroll] Error marking bonus paid:', err);
      setSnackbar({ open: true, message: `Failed to mark bonus as paid: ${err.message}`, severity: 'error' });
    } finally {
      setMarkingPaid(prev => ({ ...prev, [bonusId]: false }));
    }
  };
  
  const exportToExcel = async (historyId = null, historyMonth = null, historyYear = null) => {
    logSecurityEvent('EXPORT_ATTEMPT', { history_id: historyId, month: historyMonth || selectedMonth, year: historyYear || selectedYear });
    
    if (!isManagerOrAdmin) {
      console.warn('[Payroll] Access denied: Non-manager attempted to export');
      setSnackbar({ open: true, message: 'Access denied. Only managers can export payroll.', severity: 'error' });
      return;
    }
    
    setExporting(true);
    const startTime = performance.now();
    
    try {
      let dataToExport = [];
      let exportMonth = selectedMonth;
      let exportYear = selectedYear;
      let periodName = `${MONTHS[selectedMonth - 1]} ${selectedYear}`;
      
      if (historyId && historyMonth && historyYear) {
        exportMonth = historyMonth;
        exportYear = historyYear;
        periodName = `${MONTHS[historyMonth - 1]} ${historyYear}`;
        console.debug(`[Payroll] Exporting historical payroll: ID ${historyId}, ${periodName}`);
        
        const baseUrl = getApiBaseUrl();
        const response = await fetch(`${baseUrl}/payroll_api.php?action=get_payroll_by_period&period_id=${historyId}`);
        const result = await response.json();
        
        if (result.success && result.data) {
          dataToExport = result.data;
          console.debug(`[Payroll] Loaded ${dataToExport.length} historical records`);
        } else {
          throw new Error('Failed to load historical payroll data');
        }
      } else {
        console.debug(`[Payroll] Exporting current payroll: ${periodName}`);
        
        // Filter only employees with valid bank details for current payroll
        dataToExport = payrollData.filter(emp => 
          emp.account_number && 
          emp.account_number.trim() !== '' &&
          emp.bank_name && 
          emp.bank_name.trim() !== '' &&
          parseFloat(emp.net_salary) > 0
        );
        console.debug(`[Payroll] ${dataToExport.length} employees have valid bank details for export`);
      }
      
      if (dataToExport.length === 0) {
        setSnackbar({ open: true, message: 'No employees with valid bank details to export', severity: 'warning' });
        setExporting(false);
        return;
      }
      
      // Calculate total net salary
      const totalNetSalary = dataToExport.reduce((sum, emp) => sum + (parseFloat(emp.net_salary) || 0), 0);
      console.debug(`[Payroll] Total net salary for export: ${formatCurrency(totalNetSalary)}`);
      
      // Prepare Excel rows
      const excelRows = [];
      
      // Header row
      excelRows.push([
        'S/N',
        'BANK SORT CODE',
        'ACCT NO',
        'NAME',
        'PURPOSE',
        'SALARY',
        'BANK NAME'
      ]);
      
      // Data rows
      let serialNo = 1;
      dataToExport.forEach((emp) => {
        const sortCode = getBankSortCode(emp.bank_name);
        const accountNumber = emp.account_number ? emp.account_number.toString().trim() : '';
        const fullName = sanitizeString(emp.full_name || emp.account_name || '').toUpperCase();
        const purpose = `ELEGANTE MALL – ${MONTHS[exportMonth - 1]} Salary`;
        const salaryAmount = parseFloat(emp.net_salary) || 0;
        const bankName = sanitizeString(emp.bank_name || '');
        
        excelRows.push([
          serialNo,
          sortCode,
          accountNumber,
          fullName,
          purpose,
          salaryAmount,
          bankName
        ]);
        
        serialNo++;
      });
      
      // Add empty rows for spacing (2 empty rows as in the reference file)
      for (let i = 0; i < 2; i++) {
        excelRows.push(['', '', '', '', '', '', '']);
      }
      
      // Total row
      excelRows.push(['', '', '', '', '', totalNetSalary, '']);
      
      // Empty row after total
      excelRows.push(['', '', '', '', '', '', '']);
      
      // Employee count row
      excelRows.push([`Total Number of Employee = ${dataToExport.length}`, '', '', '', '', '', '']);
      
      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet(excelRows);
      
      // Set column widths
      ws['!cols'] = [
        { wch: 8 },   // S/N
        { wch: 15 },  // BANK SORT CODE
        { wch: 15 },  // ACCT NO
        { wch: 40 },  // NAME
        { wch: 35 },  // PURPOSE
        { wch: 18 },  // SALARY
        { wch: 20 }   // BANK NAME
      ];
      
      // Style the worksheet
      // Note: XLSX library doesn't support complex styling, but the basic structure is correct
      
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      const fileName = `Payroll_${periodName.replace(/ /g, '_')}_Bank_Format.xlsx`;
      
      XLSX.writeFile(wb, fileName);
      
      const exportDuration = (performance.now() - startTime).toFixed(2);
      console.debug(`[Payroll] Export completed in ${exportDuration}ms: ${fileName}`);
      
      setSnackbar({ 
        open: true, 
        message: `Successfully exported ${dataToExport.length} employees to ${fileName}`, 
        severity: 'success' 
      });
      
      logSecurityEvent('EXPORT_COMPLETED', { 
        file_name: fileName, 
        employee_count: dataToExport.length,
        total_amount: totalNetSalary
      });
      
    } catch (err) {
      console.error('[Payroll] Export error:', err);
      setSnackbar({ open: true, message: `Failed to export payroll data: ${err.message}`, severity: 'error' });
      logSecurityEvent('EXPORT_FAILED', { error: err.message });
    } finally {
      setExporting(false);
    }
  };
  
  const savePayroll = async () => {
    logSecurityEvent('SAVE_PAYROLL_ATTEMPT', { month: selectedMonth, year: selectedYear });
    
    if (!isManagerOrAdmin) {
      console.warn('[Payroll] Access denied: Non-manager attempted to save payroll');
      setSnackbar({ open: true, message: 'Access denied. Only managers can save payroll.', severity: 'error' });
      return;
    }
    
    // Confirm before saving
    const confirmSave = window.confirm(
      `Are you sure you want to save the payroll for ${MONTHS[selectedMonth - 1]} ${selectedYear}?\n\n` +
      `This will generate a permanent record for this period.`
    );
    if (!confirmSave) return;
    
    setLoading(true);
    const startTime = performance.now();
    
    try {
      const saveData = payrollData.map(emp => ({
        phone: sanitizeString(emp.phone),
        full_name: sanitizeString(emp.full_name),
        account_number: sanitizeString(emp.account_number || ''),
        bank_name: sanitizeString(emp.bank_name || ''),
        account_name: sanitizeString(emp.account_name || emp.full_name),
        base_salary: parseFloat(emp.base_salary) || 0,
        total_regular_deductions: parseFloat(emp.total_regular_deductions) || 0,
        total_bonuses: parseFloat(emp.total_bonuses) || 0,
        net_salary: parseFloat(emp.net_salary) || 0,
        regular_deduction_details: emp.regular_deduction_details || [],
        bonus_details: emp.bonus_details || [],
        bank_sort_code: getBankSortCode(emp.bank_name)
      }));
      
      console.debug(`[Payroll] Saving ${saveData.length} employee records`);
      
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/hotel/HR//payroll_api.php?action=generate_payroll`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          month: selectedMonth,
          year: selectedYear,
          payroll_data: saveData,
          generated_by: localStorage.getItem('userName') || 'HR Manager'
        })
      });
      
      const result = await response.json();
      console.debug(`[Payroll] Save response in ${(performance.now() - startTime).toFixed(2)}ms:`, result);
      
      if (result.success) {
        setSnackbar({ open: true, message: result.message, severity: 'success' });
        fetchPayrollHistory();
        logSecurityEvent('PAYROLL_SAVED', { 
          month: selectedMonth, 
          year: selectedYear,
          employee_count: saveData.length,
          total_amount: result.total_amount
        });
      } else {
        setSnackbar({ open: true, message: result.message || 'Failed to save payroll', severity: 'error' });
      }
    } catch (err) {
      console.error('[Payroll] Save error:', err);
      setSnackbar({ open: true, message: `Failed to save payroll: ${err.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  const openDeductionDialog = (staff) => {
    console.debug(`[Payroll] Opening deduction dialog for: ${staff.full_name}`);
    setSelectedStaff(staff);
    setDeductionAmount('');
    setDeductionReason('');
    setDeductionValidation({ valid: false, message: '', warning: null });
    setDeductionDialogOpen(true);
  };
  
  const openBonusDialog = (staff) => {
    console.debug(`[Payroll] Opening bonus dialog for: ${staff.full_name}`);
    setSelectedStaff(staff);
    setBonusAmount('');
    setBonusReason('');
    setBonusValidation({ valid: false, message: '', warning: null });
    setBonusDialogOpen(true);
  };
  
  // ==================== COMPONENT RENDER ====================
  const StatCard = ({ title, value, color, icon: Icon, subtitle }) => (
    <Card sx={{ 
      borderRadius: 3, 
      height: '100%',
      background: `linear-gradient(135deg, ${color}08 0%, ${color}02 100%)`,
      borderLeft: `4px solid ${color}`,
      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[4],
      }
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500, fontFamily: 'Times New Roman, serif' }}>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ color: color, fontFamily: 'Times New Roman, serif' }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar sx={{ bgcolor: alpha(color, 0.1), color: color, width: 48, height: 48 }}>
            <Icon />
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
  
  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f5f5f5' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <Box sx={{ 
        flexGrow: 1, 
        p: 3, 
        ml: isMobile ? 0 : sidebarOpen ? 30 : 5, 
        maxWidth: '1600px', 
        mx: 'auto', 
        width: '100%', 
        overflow: 'auto', 
        height: '100vh',
        transition: 'margin-left 0.3s ease-in-out',
      }}>
        <TopNav
          onLogout={() => {
            logSecurityEvent('USER_LOGOUT', {});
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            localStorage.removeItem('phoneNumber');
          }}
          onNotificationClick={() => {
            console.debug('[Payroll] Notification clicked');
            alert('You have new notifications!');
          }}
          toggleSidebar={toggleSidebar}
        />
        
        {/* Header Section */}
        <Paper elevation={0} sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 3, 
          background: `linear-gradient(135deg, #1B5E20 0%, #0D3B0F 100%)`, 
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '200px',
            height: '200px',
            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
            borderRadius: '50%',
          }
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>
                Payroll Generator
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5, fontFamily: 'Times New Roman, serif' }}>
                Generate monthly payroll with deductions and bonuses
              </Typography>
              {!isManagerOrAdmin && (
                <Chip 
                  label="View Only Mode" 
                  size="small" 
                  icon={<LockIcon />}
                  sx={{ mt: 1, bgcolor: alpha('#fff', 0.2), color: 'white' }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                variant="contained" 
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />} 
                onClick={fetchPayrollData}
                disabled={loading}
                sx={{ 
                  bgcolor: 'white', 
                  color: '#1B5E20', 
                  '&:hover': { bgcolor: '#f5f5f5' }, 
                  fontFamily: 'Times New Roman, serif',
                  textTransform: 'none',
                }}
              >
                {loading ? 'Loading...' : 'Refresh'}
              </Button>
            </Box>
          </Box>
        </Paper>
        
        {/* Alerts Section */}
        {showMissingAlert && missingAccounts.length > 0 && (
          <Alert 
            severity="warning" 
            sx={{ mb: 2, borderRadius: 2 }}
            onClose={() => setShowMissingAlert(false)}
            icon={<WarningIcon />}
          >
            <AlertTitle sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>
              ⚠️ {missingAccounts.length} Staff Member(s) Missing Bank Account Details
            </AlertTitle>
            <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif' }}>
              These staff members are missing bank account details. Please update their information in the salary table before payroll export.
            </Typography>
            <Accordion sx={{ mt: 1, bgcolor: 'transparent', boxShadow: 'none' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2" color="warning.main" sx={{ fontFamily: 'Times New Roman, serif' }}>
                  View Missing Accounts ({missingAccounts.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <List dense>
                  {missingAccounts.slice(0, 20).map((staff, idx) => (
                    <ListItem key={idx} divider>
                      <ListItemIcon><PersonIcon fontSize="small" color="warning" /></ListItemIcon>
                      <ListItemText 
                        primary={sanitizeString(staff.full_name)} 
                        secondary={`${staff.department || 'N/A'} - ${staff.missing_info}`}
                        primaryTypographyProps={{ fontFamily: 'Times New Roman, serif' }}
                      />
                    </ListItem>
                  ))}
                  {missingAccounts.length > 20 && (
                    <ListItem>
                      <ListItemText 
                        primary={`... and ${missingAccounts.length - 20} more`}
                        primaryTypographyProps={{ fontFamily: 'Times New Roman, serif', color: 'text.secondary' }}
                      />
                    </ListItem>
                  )}
                </List>
              </AccordionDetails>
            </Accordion>
          </Alert>
        )}
        
        {showZeroAlert && zeroSalaryStaff.length > 0 && (
          <Alert 
            severity="info" 
            sx={{ mb: 2, borderRadius: 2 }}
            onClose={() => setShowZeroAlert(false)}
            icon={<InfoIcon />}
          >
            <AlertTitle sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>
              ℹ️ {zeroSalaryStaff.length} Staff Member(s) Have Zero Salary
            </AlertTitle>
            <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif' }}>
              These staff members have ₦0.00 as their base salary. Please update their salary amount before processing payroll.
            </Typography>
            <Accordion sx={{ mt: 1, bgcolor: 'transparent', boxShadow: 'none' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2" color="info.main" sx={{ fontFamily: 'Times New Roman, serif' }}>
                  View Zero Salary Staff ({zeroSalaryStaff.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <List dense>
                  {zeroSalaryStaff.slice(0, 20).map((staff, idx) => (
                    <ListItem key={idx} divider>
                      <ListItemIcon><MoneyIcon fontSize="small" color="info" /></ListItemIcon>
                      <ListItemText 
                        primary={sanitizeString(staff.full_name)} 
                        secondary={`${staff.department || 'N/A'} - Current Salary: ${formatCurrency(staff.current_salary)}`}
                        primaryTypographyProps={{ fontFamily: 'Times New Roman, serif' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          </Alert>
        )}
        
        {/* Month/Year Selector & Export Buttons */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontFamily: 'Times New Roman, serif' }}>Month</InputLabel>
                <Select 
                  value={selectedMonth} 
                  onChange={(e) => {
                    const newMonth = e.target.value;
                    console.debug(`[Payroll] Month changed to: ${MONTHS[newMonth - 1]}`);
                    setSelectedMonth(newMonth);
                  }} 
                  label="Month" 
                  sx={{ fontFamily: 'Times New Roman, serif' }}
                >
                  {MONTHS.map((month, idx) => (
                    <MenuItem key={idx} value={idx + 1} sx={{ fontFamily: 'Times New Roman, serif' }}>
                      {month}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontFamily: 'Times New Roman, serif' }}>Year</InputLabel>
                <Select 
                  value={selectedYear} 
                  onChange={(e) => {
                    console.debug(`[Payroll] Year changed to: ${e.target.value}`);
                    setSelectedYear(e.target.value);
                  }} 
                  label="Year" 
                  sx={{ fontFamily: 'Times New Roman, serif' }}
                >
                  {YEARS.map((year) => (
                    <MenuItem key={year} value={year} sx={{ fontFamily: 'Times New Roman, serif' }}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button 
                  fullWidth 
                  variant="contained" 
                  startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />} 
                  onClick={() => exportToExcel()}
                  disabled={payrollData.length === 0 || exporting || !isManagerOrAdmin}
                  sx={{ 
                    bgcolor: '#1B5E20', 
                    '&:hover': { bgcolor: '#0D3B0F' }, 
                    fontFamily: 'Times New Roman, serif',
                    textTransform: 'none',
                  }}
                >
                  {exporting ? 'Exporting...' : 'Export as Excel (Bank Format)'}
                </Button>
                <Button 
                  fullWidth 
                  variant="outlined" 
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />} 
                  onClick={savePayroll}
                  disabled={payrollData.length === 0 || loading || !isManagerOrAdmin}
                  sx={{ 
                    borderColor: '#1B5E20', 
                    color: '#1B5E20', 
                    fontFamily: 'Times New Roman, serif',
                    textTransform: 'none',
                    '&:hover': { borderColor: '#0D3B0F', color: '#0D3B0F' }
                  }}
                >
                  {loading ? 'Saving...' : 'Save Payroll'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
        
        {/* Statistics Cards */}
        {stats && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard 
                title="Total Employees" 
                value={stats.employee_count || 0} 
                color="#1976d2" 
                icon={PersonIcon}
                subtitle={`${stats.missing_bank_count || 0} missing bank details`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard 
                title="Total Gross Salary" 
                value={formatCurrency(stats.total_gross)} 
                color="#4caf50" 
                icon={MoneyIcon}
                subtitle={`${stats.zero_salary_count || 0} have ₦0 salary`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard 
                title="Total Deductions" 
                value={formatCurrency(stats.total_deductions)} 
                color="#f44336" 
                icon={DeductIcon}
                subtitle={`${stats.deduction_count || 0} staff have deductions`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard 
                title="Total Bonuses" 
                value={formatCurrency(stats.total_bonuses)} 
                color="#ff9800" 
                icon={BonusIcon}
                subtitle={`${stats.bonus_count || 0} staff have bonuses`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard 
                title="Net Payroll" 
                value={formatCurrency(stats.total_net)} 
                color="#9c27b0" 
                icon={CheckIcon}
                subtitle={`Formula: (Salary + Bonuses) - Deductions`}
              />
            </Grid>
          </Grid>
        )}
        
        {/* Tabs */}
        <Paper sx={{ mb: 3, borderRadius: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, v) => {
              console.debug(`[Payroll] Tab changed to: ${v}`);
              setActiveTab(v);
            }} 
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              '& .MuiTab-root': { py: 2, fontWeight: 'bold', fontFamily: 'Times New Roman, serif', textTransform: 'none' } 
            }}
          >
            <Tab icon={<MoneyIcon />} iconPosition="start" label={`Payroll Summary (${payrollData.length})`} />
            <Tab icon={<DeductIcon />} iconPosition="start" label={`Deductions (${deductionsList.length})`} />
            <Tab icon={<BonusIcon />} iconPosition="start" label={`Bonuses (${bonusesList.length})`} />
            <Tab icon={<HistoryIcon />} iconPosition="start" label={`History (${payrollHistory.length})`} />
          </Tabs>
        </Paper>
        
        {/* Tab 0: Payroll Summary */}
       {/* Tab 0: Payroll Summary */}
{activeTab === 0 && (
  <>
    <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search by name, department, or phone..."
          value={searchTerm}
          onChange={(e) => {
            console.debug(`[Payroll] Search term: ${e.target.value}`);
            setSearchTerm(e.target.value);
          }}
          sx={{ flex: 2, minWidth: 200 }}
          InputProps={{ 
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
          }}
          inputProps={{ style: { fontFamily: 'Times New Roman, serif' } }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel sx={{ fontFamily: 'Times New Roman, serif' }}>Department</InputLabel>
          <Select 
            value={departmentFilter} 
            onChange={(e) => {
              console.debug(`[Payroll] Department filter: ${e.target.value}`);
              setDepartmentFilter(e.target.value);
            }} 
            label="Department" 
            sx={{ fontFamily: 'Times New Roman, serif' }}
          >
            <MenuItem value="all" sx={{ fontFamily: 'Times New Roman, serif' }}>All Departments</MenuItem>
            {uniqueDepartments.filter(d => d !== 'all').map((dept, idx) => (
              <MenuItem key={idx} value={dept} sx={{ fontFamily: 'Times New Roman, serif' }}>
                {dept}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="textSecondary" sx={{ ml: 'auto', fontFamily: 'Times New Roman, serif' }}>
          Showing {filteredData.length} of {payrollData.length} employees
        </Typography>
      </Box>
    </Paper>
    
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ color: '#1b5e20', borderLeft: '4px solid #2e7d32', pl: 2, fontFamily: 'Times New Roman, serif' }}>
        Employee Payroll Summary
      </Typography>
      <Chip 
        label={`${MONTHS[selectedMonth - 1]} ${selectedYear}`} 
        sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}
      />
    </Box>
    
    <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: theme.shadows[2] }}>
      <TableContainer sx={{ 
        maxHeight: 'calc(100vh - 380px)',  // Increased height - was 550px, now 380px gives more room
        minHeight: '500px',  // Minimum height to ensure visibility
        overflow: 'auto',
        '& .MuiTable-root': {
          minWidth: 1200,  // Ensure table doesn't collapse on small screens
        }
      }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#e8f5e9' }}>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: '#e8f5e9', fontFamily: 'Times New Roman, serif', width: 50 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: '#e8f5e9', fontFamily: 'Times New Roman, serif', width: 220 }}>Staff Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: '#e8f5e9', fontFamily: 'Times New Roman, serif', width: 180 }}>Department</TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: '#e8f5e9', fontFamily: 'Times New Roman, serif', width: 120 }}>Account</TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: '#e8f5e9', fontFamily: 'Times New Roman, serif', width: 150 }}>Bank</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#e8f5e9', fontFamily: 'Times New Roman, serif', width: 120 }}>Base Salary</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#e8f5e9', fontFamily: 'Times New Roman, serif', width: 100 }}>Bonus</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#e8f5e9', fontFamily: 'Times New Roman, serif', width: 120 }}>Deductions</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#e8f5e9', fontFamily: 'Times New Roman, serif', width: 120 }}>Net Salary</TableCell>
              {isManagerOrAdmin && (
                <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#e8f5e9', fontFamily: 'Times New Roman, serif', width: 100 }}>Actions</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={isManagerOrAdmin ? 10 : 9} align="center" sx={{ py: 8 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 1, fontFamily: 'Times New Roman, serif' }}>Loading payroll data...</Typography>
                </TableCell>
              </TableRow>
            ) : filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isManagerOrAdmin ? 10 : 9} align="center" sx={{ py: 5 }}>
                  <Typography variant="body1" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
                    No payroll data found for {MONTHS[selectedMonth - 1]} {selectedYear}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((emp, idx) => (
                <TableRow 
                  key={emp.phone} 
                  hover 
                  sx={{ 
                    bgcolor: !emp.account_number ? alpha('#ff9800', 0.05) : 'inherit',
                    '&:hover': { bgcolor: alpha('#1B5E20', 0.02) },
                    height: '72px',  // Fixed row height for consistent spacing
                  }}
                >
                  <TableCell sx={{ fontFamily: 'Times New Roman, serif', verticalAlign: 'middle' }}>{idx + 1}</TableCell>
                  <TableCell sx={{ fontFamily: 'Times New Roman, serif', verticalAlign: 'middle' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ 
                        bgcolor: (emp.total_deductions || 0) > 0 ? '#f44336' : 
                                 ((emp.total_bonuses || 0) > 0 ? '#ff9800' : '#1B5E20'),
                        width: 40,
                        height: 40,
                      }}>
                        <PersonIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium" sx={{ fontFamily: 'Times New Roman, serif' }}>
                          {sanitizeString(emp.full_name)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif', display: 'block' }}>
                          {emp.phone}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Times New Roman, serif', verticalAlign: 'middle' }}>
                    <Chip 
                      label={sanitizeString(emp.department) || 'N/A'} 
                      size="small" 
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Times New Roman, serif', verticalAlign: 'middle' }}>
                    {emp.account_number ? (
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 500 }}>
                        {emp.account_number}
                      </Typography>
                    ) : (
                      <Chip size="small" label="Missing" color="warning" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Times New Roman, serif', verticalAlign: 'middle' }}>
                    {emp.bank_name ? (
                      <Chip size="small" label={sanitizeString(emp.bank_name)} variant="outlined" />
                    ) : (
                      <Chip size="small" label="Missing" color="warning" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'Times New Roman, serif', verticalAlign: 'middle', fontWeight: 500 }}>
                    {formatCurrency(emp.base_salary)}
                  </TableCell>
                  <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
                    {(emp.total_bonuses || 0) > 0 ? (
                      <Typography variant="body2" fontWeight="bold" sx={{ color: '#ff9800', fontFamily: 'Times New Roman, serif' }}>
                        +{formatCurrency(emp.total_bonuses)}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
                    {(emp.total_deductions || 0) > 0 ? (
                      <Typography variant="body2" fontWeight="bold" sx={{ color: '#f44336', fontFamily: 'Times New Roman, serif' }}>
                        -{formatCurrency(emp.total_deductions)}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ 
                      color: (emp.net_salary || 0) > 0 ? '#1B5E20' : '#f44336', 
                      fontFamily: 'Times New Roman, serif',
                      fontSize: '0.9rem'
                    }}>
                      {formatCurrency(emp.net_salary)}
                    </Typography>
                  </TableCell>
                  {isManagerOrAdmin && (
                    <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                       
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Optional: Footer summary row */}
      {filteredData.length > 0 && !loading && (
        <Box sx={{ 
          p: 2, 
          bgcolor: '#f5f5f5', 
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 3
        }}>
          <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif' }}>
            <strong>Total Employees:</strong> {filteredData.length}
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif' }}>
            <strong>Total Gross:</strong> {formatCurrency(filteredData.reduce((sum, emp) => sum + (parseFloat(emp.base_salary) || 0), 0))}
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif' }}>
            <strong>Total Net:</strong> {formatCurrency(filteredData.reduce((sum, emp) => sum + (parseFloat(emp.net_salary) || 0), 0))}
          </Typography>
        </Box>
      )}
    </Paper>
  </>
)}
        
        {/* Tab 1: Deductions Tab */}
        {activeTab === 1 && (
          <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: theme.shadows[2] }}>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#ffebee' }}>
                  <TableRow>
                    <TableCell sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>#</TableCell>
                    <TableCell sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>Staff Name</TableCell>
                    <TableCell sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>Department</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>Amount</TableCell>
                    <TableCell sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>Reason</TableCell>
                    <TableCell sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>Approved By</TableCell>
                    <TableCell sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>Approved Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deductionsList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 5, fontFamily: 'Times New Roman, serif' }}>
                        No approved deductions for {MONTHS[selectedMonth - 1]} {selectedYear}
                      </TableCell>
                    </TableRow>
                  ) : (
                    deductionsList.map((ded, idx) => (
                      <TableRow key={ded.id} hover>
                        <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>{idx + 1}</TableCell>
                        <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>{sanitizeString(ded.full_name)}</TableCell>
                        <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>{sanitizeString(ded.department)}</TableCell>
                        <TableCell align="right" sx={{ color: '#f44336', fontWeight: 'bold', fontFamily: 'Times New Roman, serif' }}>
                          {formatCurrency(ded.amount)}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>{sanitizeString(ded.remark)}</TableCell>
                        <TableCell>
                          <Chip 
                            size="small" 
                            label={ded.status_text || 'Approved'}
                            color={ded.deduction_status === 1 ? 'success' : (ded.deduction_status === 2 ? 'error' : 'warning')}
                          />
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>{sanitizeString(ded.approved_by) || 'N/A'}</TableCell>
                        <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>{formatDate(ded.approved_date)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
        
        {/* Tab 2: Bonuses Tab */}
      {/* Tab 2: Bonuses Tab */}
{/* Tab 2: Bonuses Tab - CORRECTED VERSION */}
{activeTab === 2 && (
  <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: theme.shadows[2] }}>
    <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)', minHeight: '400px' }}>
      <Table stickyHeader size="small">
        <TableHead sx={{ bgcolor: '#fff3e0' }}>
          <TableRow>
            <TableCell sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold', width: 50 }}>#</TableCell>
            <TableCell sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold', width: 200 }}>Staff Name</TableCell>
            <TableCell sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold', width: 150 }}>Department</TableCell>
            <TableCell align="right" sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold', width: 120 }}>Bonus Amount</TableCell>
            <TableCell sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold', width: 250 }}>Reason</TableCell>
            <TableCell sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold', width: 120 }}>Approval Status</TableCell>
            <TableCell sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold', width: 100 }}>Paid Date</TableCell>
            <TableCell sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold', width: 120 }}>Paid By</TableCell>
            <TableCell align="center" sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold', width: 120 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {bonusesList.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 5, fontFamily: 'Times New Roman, serif' }}>
                No bonuses for {MONTHS[selectedMonth - 1]} {selectedYear}
              </TableCell>
            </TableRow>
          ) : (
            bonusesList.map((bonus, idx) => {
              // Check payment status from multiple possible field names
              const isPaid = bonus.bonus_paid === 1 || bonus.is_paid === 1 || bonus.paid_date !== null || bonus.bonus_paid === '1';
              const paidDate = bonus.bonus_paid_date || bonus.paid_date;
              const paidBy = bonus.bonus_paid_by || bonus.paid_by;
              const bonusAmount = bonus.recent_bonus || bonus.bonus_amount || 0;
              
              console.debug(`[Payroll] Bonus ${bonus.id}: isPaid=${isPaid}, paidDate=${paidDate}, bonus_paid=${bonus.bonus_paid}, is_paid=${bonus.is_paid}`);
              
              return (
                <TableRow 
                  key={bonus.id} 
                  hover 
                  sx={{ 
                    bgcolor: isPaid ? alpha('#4caf50', 0.05) : 'inherit',
                    height: '68px'
                  }}
                >
                  <TableCell sx={{ fontFamily: 'Times New Roman, serif', verticalAlign: 'middle' }}>{idx + 1}</TableCell>
                  <TableCell sx={{ fontFamily: 'Times New Roman, serif', verticalAlign: 'middle' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ 
                        bgcolor: isPaid ? '#4caf50' : '#ff9800',
                        width: 32,
                        height: 32,
                      }}>
                        <BonusIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium" sx={{ fontFamily: 'Times New Roman, serif' }}>
                          {sanitizeString(bonus.full_name)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
                          {bonus.phone}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Times New Roman, serif', verticalAlign: 'middle' }}>
                    <Chip 
                      label={sanitizeString(bonus.department) || 'N/A'} 
                      size="small" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'Times New Roman, serif', verticalAlign: 'middle', fontWeight: 'bold', color: '#ff9800' }}>
                    {formatCurrency(bonusAmount)}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Times New Roman, serif', verticalAlign: 'middle' }}>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {sanitizeString(bonus.bonus_reason || bonus.reason)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'middle' }}>
                    <Chip 
                      size="small" 
                      label={bonus.approval_status === 'approved' ? 'Approved' : (bonus.approval_status === 'rejected' ? 'Rejected' : 'Pending')}
                      color={bonus.approval_status === 'approved' ? 'success' : (bonus.approval_status === 'rejected' ? 'error' : 'warning')}
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Times New Roman, serif', verticalAlign: 'middle' }}>
                    {isPaid ? (
                      <Chip 
                        size="small" 
                        label={paidDate ? formatDate(paidDate) : 'Paid'} 
                        color="success" 
                        variant="outlined"
                        icon={<CheckIcon sx={{ fontSize: 14 }} />}
                      />
                    ) : (
                      <Typography variant="caption" color="textSecondary">Not Paid</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Times New Roman, serif', verticalAlign: 'middle' }}>
                    {sanitizeString(paidBy) || '-'}
                  </TableCell>
                  <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                    {bonus.approval_status === 'approved' && !isPaid && isManagerOrAdmin ? (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={markingPaid[bonus.id] ? <CircularProgress size={16} color="inherit" /> : <PaidIcon />}
                        onClick={() => handleMarkBonusPaid(bonus.id, bonus.full_name, bonusAmount)}
                        disabled={markingPaid[bonus.id]}
                        sx={{ 
                          bgcolor: '#4caf50', 
                          '&:hover': { bgcolor: '#388e3c' }, 
                          fontFamily: 'Times New Roman, serif',
                          textTransform: 'none',
                          fontSize: '0.7rem',
                          px: 1.5,
                          py: 0.5
                        }}
                      >
                        {markingPaid[bonus.id] ? 'Processing...' : 'Mark Paid'}
                      </Button>
                    ) : bonus.approval_status === 'approved' && isPaid ? (
                      <Chip 
                        size="small" 
                        label="Paid ✓" 
                        color="success" 
                        variant="filled"
                        sx={{ fontWeight: 'bold' }}
                      />
                    ) : bonus.approval_status !== 'approved' ? (
                      <Chip 
                        size="small" 
                        label="Awaiting Approval" 
                        color="warning" 
                        variant="outlined"
                      />
                    ) : !isManagerOrAdmin ? (
                      <Chip 
                        size="small" 
                        label={isPaid ? "Paid" : "Pending Payment"} 
                        color={isPaid ? "success" : "warning"} 
                        variant="outlined"
                      />
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
    
    {/* Bonus Summary Footer - FIXED VERSION */}
    {bonusesList.length > 0 && (
      <Box sx={{ 
        p: 2, 
        bgcolor: '#fff8e1', 
        borderTop: '1px solid #ffe0b2',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2
      }}>
        {(() => {
          // Re-calculate totals directly from bonusesList for accuracy
          const totalAllBonuses = bonusesList.reduce((sum, b) => sum + (parseFloat(b.recent_bonus || b.bonus_amount || 0)), 0);
          
          // FIXED: Check payment status consistently with the same logic as the table
          const paidBonusesList = bonusesList.filter(b => {
            const isPaid = b.bonus_paid === 1 || b.is_paid === 1 || b.paid_date !== null || b.bonus_paid === '1';
            return isPaid;
          });
          
          const unpaidBonusesList = bonusesList.filter(b => {
            const isPaid = b.bonus_paid === 1 || b.is_paid === 1 || b.paid_date !== null || b.bonus_paid === '1';
            return !isPaid && b.approval_status === 'approved';
          });
          
          const totalPaid = paidBonusesList.reduce((sum, b) => sum + (parseFloat(b.recent_bonus || b.bonus_amount || 0)), 0);
          const totalUnpaid = unpaidBonusesList.reduce((sum, b) => sum + (parseFloat(b.recent_bonus || b.bonus_amount || 0)), 0);
          
          console.debug(`[Payroll] Summary: Total=${totalAllBonuses}, Paid=${totalPaid} (${paidBonusesList.length} records), Unpaid=${totalUnpaid} (${unpaidBonusesList.length} records)`);
          
          return (
            <>
              <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif' }}>
                <strong>Total Bonuses:</strong> {formatCurrency(totalAllBonuses)}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif', color: '#f44336' }}>
                <strong>Unpaid Bonuses:</strong> {formatCurrency(totalUnpaid)} ({unpaidBonusesList.length} pending)
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif', color: '#4caf50' }}>
                <strong>Paid Bonuses:</strong> {formatCurrency(totalPaid)} ({paidBonusesList.length} paid)
              </Typography>
            </>
          );
        })()}
      </Box>
    )}
  </Paper>
)}


        
        {/* Tab 3: History Tab */}
        {activeTab === 3 && (
          <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: theme.shadows[2] }}>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#e3f2fd' }}>
                  <TableRow>
                    <TableCell sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>Period</TableCell>
                    <TableCell sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>Month</TableCell>
                    <TableCell sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>Year</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>Total Amount</TableCell>
                    <TableCell align="center" sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>Employees</TableCell>
                    <TableCell sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>Generated By</TableCell>
                    <TableCell sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>Generated Date</TableCell>
                    <TableCell align="center" sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payrollHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 5, fontFamily: 'Times New Roman, serif' }}>
                        No payroll history found
                      </TableCell>
                    </TableRow>
                  ) : (
                    payrollHistory.map((history) => (
                      <TableRow key={history.id} hover>
                        <TableCell sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>
                          {MONTHS[history.month - 1]} {history.year}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>{MONTHS[history.month - 1]}</TableCell>
                        <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>{history.year}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'Times New Roman, serif', fontWeight: 'bold' }}>
                          {formatCurrency(history.total_amount)}
                        </TableCell>
                        <TableCell align="center" sx={{ fontFamily: 'Times New Roman, serif' }}>{history.employee_count}</TableCell>
                        <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>{sanitizeString(history.generated_by) || 'N/A'}</TableCell>
                        <TableCell sx={{ fontFamily: 'Times New Roman, serif' }}>{formatDate(history.generated_date)}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Export to Bank Format">
                            <IconButton 
                              size="small" 
                              color="success"
                              onClick={() => exportToExcel(history.id, history.month, history.year)}
                              disabled={!isManagerOrAdmin}
                              sx={{ 
                                bgcolor: alpha('#1B5E20', 0.1), 
                                '&:hover': { bgcolor: alpha('#1B5E20', 0.2) } 
                              }}
                            >
                              <FileDownloadIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
        
        {/* Footer */}
        <footer style={{ textAlign: 'center', padding: '20px', marginTop: 'auto' }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
            © {new Date().getFullYear()} Elegantè IT - Payroll System. All rights reserved.
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
            Version 5.0 | Secure Payroll Management System
          </Typography>
        </footer>
      </Box>
      
      {/* Deduction Dialog */}
      <Dialog 
        open={deductionDialogOpen} 
        onClose={() => {
          console.debug('[Payroll] Closing deduction dialog');
          setDeductionDialogOpen(false);
          setSelectedStaff(null);
          setDeductionAmount('');
          setDeductionReason('');
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#f44336', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeductIcon /> Add Salary Deduction
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            {selectedStaff && (
              <Alert severity="info" sx={{ fontFamily: 'Times New Roman, serif' }}>
                <strong>Staff:</strong> {sanitizeString(selectedStaff.full_name)}<br />
                <strong>Department:</strong> {sanitizeString(selectedStaff.department)}<br />
                <strong>Base Salary:</strong> {formatCurrency(selectedStaff.base_salary)}
              </Alert>
            )}
            <TextField
              fullWidth
              label="Deduction Amount (₦)"
              type="number"
              value={deductionAmount}
              onChange={(e) => setDeductionAmount(e.target.value)}
              placeholder="Enter amount to deduct"
              InputProps={{ 
                startAdornment: <InputAdornment position="start">₦</InputAdornment>,
              }}
              error={deductionValidation.message && !deductionValidation.valid}
              helperText={deductionValidation.message || (deductionValidation.warning ? deductionValidation.warning : '')}
            />
            {deductionValidation.warning && !deductionValidation.message && (
              <FormHelperText error={false} sx={{ color: '#ff9800' }}>
                ⚠️ {deductionValidation.warning}
              </FormHelperText>
            )}
            <TextField
              fullWidth
              label="Reason for Deduction"
              multiline
              rows={3}
              value={deductionReason}
              onChange={(e) => setDeductionReason(e.target.value)}
              placeholder="e.g., Late coming, Absenteeism, Damage to company property, etc."
              helperText="Provide a clear and detailed reason for this deduction (minimum 3 characters)"
            />
            <Alert severity="warning" sx={{ fontFamily: 'Times New Roman, serif' }}>
              <strong>Note:</strong> This deduction will be applied to the {MONTHS[selectedMonth - 1]} {selectedYear} payroll and requires manager approval.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeductionDialogOpen(false);
              setSelectedStaff(null);
            }}
            sx={{ fontFamily: 'Times New Roman, serif' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddDeduction} 
            variant="contained" 
            sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#d32f2f' }, fontFamily: 'Times New Roman, serif' }} 
            disabled={loading || !deductionAmount || !deductionReason.trim()}
          >
            {loading ? <CircularProgress size={24} /> : 'Add Deduction'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Bonus Dialog */}
      <Dialog 
        open={bonusDialogOpen} 
        onClose={() => {
          console.debug('[Payroll] Closing bonus dialog');
          setBonusDialogOpen(false);
          setSelectedStaff(null);
          setBonusAmount('');
          setBonusReason('');
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#ff9800', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BonusIcon /> Add Salary Bonus
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            {selectedStaff && (
              <Alert severity="info" sx={{ fontFamily: 'Times New Roman, serif' }}>
                <strong>Staff:</strong> {sanitizeString(selectedStaff.full_name)}<br />
                <strong>Department:</strong> {sanitizeString(selectedStaff.department)}<br />
                <strong>Base Salary:</strong> {formatCurrency(selectedStaff.base_salary)}
              </Alert>
            )}
            <TextField
              fullWidth
              label="Bonus Amount (₦)"
              type="number"
              value={bonusAmount}
              onChange={(e) => setBonusAmount(e.target.value)}
              placeholder="Enter bonus amount"
              InputProps={{ 
                startAdornment: <InputAdornment position="start">₦</InputAdornment>,
              }}
              error={bonusValidation.message && !bonusValidation.valid}
              helperText={bonusValidation.message || (bonusValidation.warning ? bonusValidation.warning : '')}
            />
            {bonusValidation.warning && !bonusValidation.message && (
              <FormHelperText error={false} sx={{ color: '#ff9800' }}>
                ⚠️ {bonusValidation.warning}
              </FormHelperText>
            )}
            <TextField
              fullWidth
              label="Reason for Bonus"
              multiline
              rows={3}
              value={bonusReason}
              onChange={(e) => setBonusReason(e.target.value)}
              placeholder="e.g., Performance bonus, End of year bonus, Outstanding achievement, etc."
              helperText="Provide a clear and detailed reason for this bonus (minimum 3 characters)"
            />
            <Alert severity="info" sx={{ fontFamily: 'Times New Roman, serif' }}>
              <strong>Note:</strong> Bonus requests require manager approval. Once approved, they can be marked as paid.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setBonusDialogOpen(false);
              setSelectedStaff(null);
            }}
            sx={{ fontFamily: 'Times New Roman, serif' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddBonus} 
            variant="contained" 
            sx={{ bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' }, fontFamily: 'Times New Roman, serif' }} 
            disabled={loading || !bonusAmount || !bonusReason.trim()}
          >
            {loading ? <CircularProgress size={24} /> : 'Submit Bonus Request'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={5000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ fontFamily: 'Times New Roman, serif', minWidth: '300px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PayrollGenerator;